/**
 * Hooks for managing document shares
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { DocSidebarData } from "@/lib/erp/docs";

/**
 * Update or create a DocShare entry for a user on a document
 */
export function useUpdateDocShare(doctype: string, name: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      user: string;
      can_read?: boolean;
      can_write?: boolean;
      can_share?: boolean;
      can_submit?: boolean;
    }) => {
      return erp.docs.updateDocShare({
        doctype,
        name,
        ...params,
      });
    },
    onSuccess: (data) => {
      // Optimistically update the sidebar query with full DocSidebarData
      queryClient.setQueryData<DocSidebarData>(
        ["doc-sidebar", doctype, name],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            shares: data.shares,
            share_capabilities: data.share_capabilities,
          };
        }
      );
      // Also invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    },
  });
}

/**
 * Delete a DocShare entry for a user on a document
 */
export function useDeleteDocShare(doctype: string, name: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { user: string }) => {
      return erp.docs.deleteDocShare({
        doctype,
        name,
        ...params,
      });
    },
    onSuccess: (data) => {
      // Optimistically update the sidebar query with full DocSidebarData
      queryClient.setQueryData<DocSidebarData>(
        ["doc-sidebar", doctype, name],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            shares: data.shares,
          };
        }
      );
      // Also invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    },
  });
}

/**
 * Update or create the "Everyone" (public) share for a document
 */
export function useUpdatePublicShare(doctype: string, name: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      can_read: boolean;
      can_write?: boolean;
      can_share?: boolean;
      can_submit?: boolean;
    }) => {
      return erp.docs.updatePublicShare({
        doctype,
        name,
        ...params,
      });
    },
    onSuccess: (data) => {
      // Optimistically update the sidebar query with full DocSidebarData
      queryClient.setQueryData<DocSidebarData>(
        ["doc-sidebar", doctype, name],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            public_share: data.public_share,
            share_capabilities: data.share_capabilities,
          };
        }
      );
      // Also invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    },
  });
}

